/**
 * AP-23 (Phase D) — Standard-Aktionen für ein Listen-Layout.
 *
 * Sitzt im Konfigurationsmodus der Filterleiste und der Spaltenauswahl und bietet
 * dort dreierlei:
 *   - **Auf Standard zurücksetzen** (jeder): löscht die eigene Einstellung, danach
 *     greift der Mandanten-Standard bzw. der Code-Default der Liste.
 *   - **Als Mandanten-Standard speichern** (nur ADMIN): der aktuelle Stand wird
 *     zum Startwert für alle, die noch nichts Eigenes eingestellt haben.
 *   - ein dezenter Hinweis, wenn das eigene Layout vom Standard abweicht.
 *
 * Gepflegt wird der Standard bewusst **aus der Liste heraus** — niemand tippt
 * Spaltenschlüssel in einer Einstellungsmaske ab. Die Maske in den
 * Mandanten-Einstellungen zeigt nur, was gesetzt ist, und erlaubt Entfernen.
 *
 * Die Komponente liegt in `src/components/`, weil sie von der `FilterBar` **und**
 * von vier Listenseiten aus verschiedenen Features benutzt wird.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RotateCcw, Building2, Check, AlertCircle } from 'lucide-react'
import {
  resetUiSetting,
  saveTenantDefault,
  differsFromTenantDefault,
  loadTenantDefault,
  canManageTenantDefaults,
} from '@/lib/ui-settings'

interface LayoutDefaultActionsProps {
  /** Schlüssel der Einstellung, z. B. `columnbar_layout_summary` */
  settingKey: string
  /** Aktueller Stand — das, was als Mandanten-Standard gespeichert würde */
  currentValue: unknown
  /**
   * Wird nach dem Zurücksetzen mit dem ab jetzt gültigen Wert aufgerufen:
   * dem Mandanten-Standard, oder `null` für „nimm den Code-Default".
   */
  onReset: (value: unknown | null) => void
  className?: string
}

export function LayoutDefaultActions({
  settingKey,
  currentValue,
  onReset,
  className = '',
}: LayoutDefaultActionsProps) {
  const { t } = useTranslation()
  // Rolle aus der Bibliothek statt aus dem auth.store: Geteilte Komponenten
  // greifen nicht in Features hinein (AP-21-B).
  const isAdmin = canManageTenantDefaults()

  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(false)
  // Speichern und Zurücksetzen ändern den Standard im Cache, nicht im React-State.
  // `tick` zwingt die beiden Abfragen darunter zur Neubewertung, ohne dass die
  // Seite neu geladen werden muss.
  const [tick, setTick] = useState(0)

  void tick // beide Abfragen lesen den Cache; `tick` erzwingt das erneute Rendern
  const hasTenantDefault = loadTenantDefault(settingKey) != null
  const differs = differsFromTenantDefault(settingKey, currentValue)

  const handleReset = () => {
    onReset(resetUiSetting(settingKey))
    setTick((n) => n + 1)
  }

  const handleSaveAsDefault = async () => {
    setError(false)
    try {
      await saveTenantDefault(settingKey, currentValue)
      setSaved(true)
      setTick((n) => n + 1)
      setTimeout(() => setSaved(false), 2500)
    } catch {
      setError(true)
    }
  }

  const btn =
    'inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground'

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <button type="button" onClick={handleReset} className={btn}>
        <RotateCcw className="h-3.5 w-3.5" />
        {t('uiSettings.resetToDefault', { defaultValue: 'Auf Standard zurücksetzen' })}
      </button>

      {isAdmin && (
        <button
          type="button"
          onClick={handleSaveAsDefault}
          className={btn}
          title={t('uiSettings.saveAsTenantDefaultHint', {
            defaultValue:
              'Gilt als Startwert für alle, die noch keine eigene Einstellung haben. Bestehende Einrichtungen bleiben unberührt.',
          })}
        >
          {saved ? <Check className="h-3.5 w-3.5 text-primary" /> : <Building2 className="h-3.5 w-3.5" />}
          {saved
            ? t('uiSettings.savedAsTenantDefault', { defaultValue: 'Als Standard gespeichert' })
            : t('uiSettings.saveAsTenantDefault', { defaultValue: 'Als Mandanten-Standard speichern' })}
        </button>
      )}

      {error && (
        <span className="inline-flex items-center gap-1 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          {t('uiSettings.saveFailed', { defaultValue: 'Speichern fehlgeschlagen' })}
        </span>
      )}

      {hasTenantDefault && differs && !saved && (
        <span className="text-xs text-muted-foreground">
          {t('uiSettings.differsFromDefault', {
            defaultValue: 'Weicht vom Mandanten-Standard ab',
          })}
        </span>
      )}
    </div>
  )
}
